"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingDown, Settings2, ArrowLeftRight, ShoppingCart, Plus, Minus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { DataTable, type Column } from "../DataTable";
import { useERP, formatBDT, formatNum, type ID } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";
import { CSVExportButton } from "../PrintExport";

export function InventoryModule() {
  const erp = useERP();
  const { toast } = useToast();
  const [tab, setTab] = React.useState("stock");
  const [adjustOpen, setAdjustOpen] = React.useState(false);
  const [transferOpen, setTransferOpen] = React.useState(false);
  const tabs: SimpleTab[] = [
    { value: "stock", label: "Stock by Product" },
    { value: "ledger", label: "Stock Ledger", count: erp.stockLedger.length },
    { value: "wh", label: "Warehouse Stock" },
    { value: "reorder", label: "Reorder List" },
  ];

  // Create a quick PO for reorder
  const createReorderPO = (productId: ID, qty: number) => {
    const product = erp.getProduct(productId);
    if (!product) return;
    const supplier = erp.suppliers.find(s => s.category === product.category) ?? erp.suppliers[0];
    if (!supplier) { toast({ title: "No supplier available", variant: "destructive" }); return; }
    // M6 fix: capture PO number from the new state — use nextNumber preview
    const poId = erp.createPO({
      supplierId: supplier.id,
      warehouseId: erp.warehouses[0]?.id ?? "",
      orderDate: new Date().toISOString().slice(0, 10),
      expectedDelivery: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10),
      lines: [{ productId, qty: Math.max(qty, 10), unitPrice: product.costPrice, receivedQty: 0 }],
      total: Math.max(qty, 10) * product.costPrice,
      notes: `Auto-reorder PO for low-stock item`,
    });
    // Read the freshly-created PO from updated state
    const newPO = erp.purchaseOrders.find(p => p.id === poId);
    toast({ title: "PO Created", description: `PO ${newPO?.poNumber ?? "(pending)"} created for ${product.name} (${Math.max(qty, 10)} units). Pending approval.` });
  };

  const productStock = erp.products.map(p => {
    const stock = erp.getStock(p.id);
    return {
      id: p.id, sku: p.sku, name: p.name, category: p.category,
      stock, value: stock * p.costPrice, reorderPoint: p.reorderPoint,
      status: stock < p.reorderPoint ? "Reorder" : stock < p.reorderPoint * 2 ? "Low" : "OK",
    };
  });

  const whStock = erp.warehouses.map(w => {
    const entries = erp.stockLedger.filter(e => e.warehouseId === w.id);
    const stock = entries.reduce((s, e) => s + e.qty, 0);
    return { id: w.id, code: w.code, name: w.name, type: w.type, branch: w.branch, stock, utilization: w.utilization, capacity: w.capacity };
  });

  const reorderList = productStock.filter(p => p.status !== "OK");

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">Inventory</h1>
        <p className="text-muted-foreground mt-1 text-sm">Live stock position across all warehouses with reorder alerts.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Total SKUs</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{formatNum(erp.products.length)}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Stock Value</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{formatBDT(productStock.reduce((s, p) => s + p.value, 0))}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Reorder Items</CardTitle></CardHeader>
          <CardContent><div className="text-destructive text-2xl font-semibold">{formatNum(reorderList.length)}</div></CardContent></Card>
        <Card><CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">Total Stock Qty</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-semibold">{formatNum(productStock.reduce((s, p) => s + p.stock, 0))}</div></CardContent></Card>
      </div>

      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />

      {tab === "stock" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Stock by Product</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setAdjustOpen(true)}><Settings2 className="size-4 mr-1" /> Adjust Stock</Button>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={[
                { key: "sku", header: "SKU", sortable: true, render: r => <span className="font-mono text-xs">{r.sku}</span> },
                { key: "name", header: "Product", sortable: true, render: r => <span className="font-medium">{r.name}</span> },
                { key: "category", header: "Category" },
                { key: "stock", header: "Stock Qty", align: "right", sortable: true, render: r => formatNum(r.stock) },
                { key: "value", header: "Stock Value", align: "right", sortable: true, render: r => formatBDT(r.value) },
                { key: "reorderPoint", header: "Reorder Pt", align: "right" },
                { key: "status", header: "Status", render: r => <Badge variant={r.status === "Reorder" ? "destructive" : r.status === "Low" ? "secondary" : "default"}>{r.status}</Badge> },
              ] as Column<typeof productStock[number]>[]}
              data={productStock}
              rowKey={r => r.id}
              searchKeys={["sku", "name", "category"]}
            />
          </CardContent>
        </Card>
      )}

      {tab === "ledger" && (
        <Card>
          <CardHeader><CardTitle>Stock Ledger Entries</CardTitle></CardHeader>
          <CardContent>
            <DataTable
              columns={[
                { key: "date", header: "Date", sortable: true },
                { key: "product", header: "Product", render: r => erp.getProduct(r.productId)?.name ?? "—" },
                { key: "warehouse", header: "Warehouse", render: r => erp.getWarehouse(r.warehouseId)?.name ?? "—" },
                { key: "movementType", header: "Movement Type" },
                { key: "refType", header: "Ref Type" },
                { key: "qty", header: "Qty", align: "right", sortable: true, render: r => <span className={r.qty < 0 ? "text-destructive font-medium" : "text-primary font-medium"}>{r.qty > 0 ? "+" : ""}{formatNum(r.qty)}</span> },
                { key: "balanceAfter", header: "Balance", align: "right" },
                { key: "batchNo", header: "Batch", render: r => r.batchNo ?? "—" },
              ] as Column<typeof erp.stockLedger[number]>[]}
              data={erp.stockLedger}
              rowKey={r => r.id}
              searchKeys={["movementType", "refType", "batchNo"]}
              pageSize={15}
            />
          </CardContent>
        </Card>
      )}

      {tab === "wh" && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Warehouse Stock</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setTransferOpen(true)}><ArrowLeftRight className="size-4 mr-1" /> Transfer Stock</Button>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={[
                { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
                { key: "name", header: "Warehouse", sortable: true },
                { key: "type", header: "Type" },
                { key: "branch", header: "Branch" },
                { key: "capacity", header: "Capacity", align: "right" },
                { key: "utilization", header: "Utilization", align: "right", sortable: true, render: r => <span className={r.utilization > 80 ? "text-destructive font-medium" : ""}>{r.utilization}%</span> },
                { key: "stock", header: "Stock Qty", align: "right", sortable: true, render: r => formatNum(r.stock) },
              ] as Column<typeof whStock[number]>[]}
              data={whStock}
              rowKey={r => r.id}
              searchKeys={["code", "name", "branch"]}
            />
          </CardContent>
        </Card>
      )}

      {tab === "reorder" && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="text-destructive size-5" /> Reorder List</CardTitle></CardHeader>
          <CardContent>
            {reorderList.length === 0 ? (
              <div className="text-muted-foreground py-8 text-center">All products are above reorder point.</div>
            ) : (
              <DataTable
                columns={[
                  { key: "sku", header: "SKU", sortable: true, render: r => <span className="font-mono text-xs">{r.sku}</span> },
                  { key: "name", header: "Product", sortable: true },
                  { key: "stock", header: "Current Stock", align: "right", render: r => <span className="text-destructive font-semibold">{formatNum(r.stock)}</span> },
                  { key: "reorderPoint", header: "Reorder Pt", align: "right" },
                  { key: "shortfall", header: "Shortfall", align: "right", render: r => <span className="text-destructive">{formatNum(Math.max(0, r.reorderPoint - r.stock))}</span> },
                  { key: "status", header: "Status", render: r => <Badge variant={r.status === "Reorder" ? "destructive" : "secondary"}>{r.status}</Badge> },
                  { key: "actions", header: "Actions", align: "right", render: r => (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { createReorderPO(r.id, r.reorderPoint - r.stock + 50); }}><ShoppingCart className="size-3 mr-1" /> Create PO</Button>
                  )},
                ] as Column<typeof reorderList[number]>[]}
                data={reorderList}
                rowKey={r => r.id}
                searchKeys={["sku", "name"]}
              />
            )}
          </CardContent>
        </Card>
      )}

      <AdjustStockDialog open={adjustOpen} onOpenChange={setAdjustOpen} />
      <TransferStockDialog open={transferOpen} onOpenChange={setTransferOpen} />
    </div>
  );
}

// ===== Adjust Stock Dialog =====
function AdjustStockDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [productId, setProductId] = React.useState("");
  const [warehouseId, setWarehouseId] = React.useState("");
  const [qtyDelta, setQtyDelta] = React.useState("");
  const [reason, setReason] = React.useState("");
  React.useEffect(() => { if (open) { setProductId(erp.products[0]?.id ?? ""); setWarehouseId(erp.warehouses[0]?.id ?? ""); setQtyDelta(""); setReason(""); } }, [open]);
  const currentStock = productId && warehouseId ? erp.getStock(productId, warehouseId) : 0;
  const product = erp.products.find(p => p.id === productId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Stock Adjustment</DialogTitle>
        <DialogDescription>Record manual stock adjustment (gain/loss/damage/cycle count).</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{erp.products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Warehouse</Label>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{erp.warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="rounded-md border bg-muted/30 p-2 text-sm flex justify-between">
            <span className="text-muted-foreground">Current Stock:</span><span className="font-semibold">{formatNum(currentStock)} {product?.uom}</span>
          </div>
          <div>
            <Label htmlFor="qty-delta">Quantity Change (+ / -)</Label>
            <Input id="qty-delta" type="number" value={qtyDelta} onChange={e => setQtyDelta(e.target.value)} placeholder="e.g. -5 or +20" autoFocus />
          </div>
          <div>
            <Label htmlFor="rsn">Reason</Label>
            <Input id="rsn" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Damage, Cycle count adjustment" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => {
            const delta = Number(qtyDelta) || 0;
            if (!productId || !warehouseId || delta === 0 || !reason.trim()) { toast({ title: "All fields required", variant: "destructive" }); return; }
            erp.adjustStock(productId, warehouseId, delta, reason.trim(), "Md. Hasan");
            toast({ title: "Stock Adjusted", description: `${delta > 0 ? "+" : ""}${delta} ${product?.uom} for ${product?.name}.` });
            onOpenChange(false);
          }}><Settings2 className="size-4 mr-1" /> Adjust</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===== Transfer Stock Dialog =====
function TransferStockDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const [productId, setProductId] = React.useState("");
  const [fromWh, setFromWh] = React.useState("");
  const [toWh, setToWh] = React.useState("");
  const [qty, setQty] = React.useState("");
  React.useEffect(() => { if (open) { setProductId(erp.products[0]?.id ?? ""); setFromWh(erp.warehouses[0]?.id ?? ""); setToWh(erp.warehouses[1]?.id ?? ""); setQty(""); } }, [open]);
  const product = erp.products.find(p => p.id === productId);
  const fromStock = productId && fromWh ? erp.getStock(productId, fromWh) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Stock Transfer</DialogTitle>
        <DialogDescription>Move stock between warehouses. Two ledger entries will be posted.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Product</Label>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>{erp.products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>From Warehouse</Label>
              <Select value={fromWh} onValueChange={setFromWh}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{erp.warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>To Warehouse</Label>
              <Select value={toWh} onValueChange={setToWh}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>{erp.warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="rounded-md border bg-muted/30 p-2 text-sm flex justify-between">
            <span className="text-muted-foreground">Available at From-Wh:</span><span className="font-semibold">{formatNum(fromStock)} {product?.uom}</span>
          </div>
          <div>
            <Label htmlFor="tqty">Quantity to Transfer</Label>
            <Input id="tqty" type="number" value={qty} onChange={e => setQty(e.target.value)} placeholder="0" autoFocus />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => {
            const q = Number(qty) || 0;
            if (!productId || !fromWh || !toWh || q <= 0 || fromWh === toWh) { toast({ title: "Invalid transfer", variant: "destructive" }); return; }
            if (q > fromStock) { toast({ title: "Insufficient stock", description: `Only ${fromStock} available.`, variant: "destructive" }); return; }
            erp.transferStock(productId, fromWh, toWh, q, "Md. Hasan");
            toast({ title: "Stock Transferred", description: `${q} ${product?.uom} moved to ${erp.getWarehouse(toWh)?.name}.` });
            onOpenChange(false);
          }}><ArrowLeftRight className="size-4 mr-1" /> Transfer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
